const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

type TurnstileRenderOptions = {
  sitekey: string;
  action?: string;
  appearance?: 'always' | 'execute' | 'interaction-only';
  theme?: 'auto' | 'light' | 'dark';
  size?: 'normal' | 'flexible' | 'compact';
  execution?: 'render' | 'execute';
  callback?: (token: string) => void;
  'error-callback'?: () => void;
  'expired-callback'?: () => void;
  'timeout-callback'?: () => void;
};

export type TurnstileApi = {
  ready: (callback: () => void) => void;
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string;
  reset: (widgetId?: string) => void;
  remove: (widgetId?: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let turnstilePromise: Promise<TurnstileApi> | null = null;

export function loadTurnstile(): Promise<TurnstileApi> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('turnstile-unavailable'));
  }
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (turnstilePromise) return turnstilePromise;

  turnstilePromise = new Promise((resolve, reject) => {
    let failed = false;
    const finish = () => {
      const started = Date.now();
      const tick = () => {
        if (failed) return;
        if (window.turnstile) {
          resolve(window.turnstile);
          return;
        }
        if (Date.now() - started > 8000) {
          turnstilePromise = null;
          reject(new Error('turnstile-unavailable'));
          return;
        }
        window.setTimeout(tick, 50);
      };
      tick();
    };
    const fail = () => {
      if (failed) return;
      failed = true;
      turnstilePromise = null;
      reject(new Error('turnstile-script-failed'));
    };

    const existing = document.querySelector<HTMLScriptElement>('script[data-turnstile="true"]');
    if (existing) {
      finish();
      existing.addEventListener('error', fail, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = TURNSTILE_SCRIPT_SRC;
    // Dynamic scripts default to async. Turnstile's ready() rejects async/defer tags.
    script.async = false;
    script.dataset.turnstile = 'true';
    script.onload = finish;
    script.onerror = fail;
    document.head.appendChild(script);
  });

  return turnstilePromise;
}

export const NEWSLETTER_SUBSCRIBE_ACTION = 'newsletter_subscribe';

export const PRODUCTION_NEWSLETTER_SUBSCRIBE_URL = 'https://traffic-lite-production.netlify.app/api/newsletter-subscribe';

export function newsletterSubscribeUrl(hostname = typeof window === 'undefined' ? '' : window.location.hostname): string {
  const host = String(hostname || '').toLowerCase();
  if (host === 'localhost' || host === '127.0.0.1') {
    return '/api/newsletter-subscribe';
  }
  return PRODUCTION_NEWSLETTER_SUBSCRIBE_URL;
}
